import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import { useRole } from '../../context/RoleContext';
import { useAuth } from '../../context/AuthContext';
import WorkOrderSheet from '../../features/clients/components/profile/WorkOrderSheet';

// Mirrors DispatchMobileSummary's status palette so both mobile surfaces feel consistent.
const STATUS_CFG = {
  approved:   { label: 'Ready',      dot: 'bg-gray-300',    text: 'text-gray-500'    },
  scheduled:  { label: 'Scheduled',  dot: 'bg-gray-300',    text: 'text-gray-500'    },
  en_route:   { label: 'En Route',   dot: 'bg-amber-500',   text: 'text-amber-700'   },
  on_site:    { label: 'On Site',    dot: 'bg-indigo-500',  text: 'text-indigo-700'  },
  in_progress:{ label: 'In Progress',dot: 'bg-indigo-500',  text: 'text-indigo-700'  },
  completed:  { label: 'Done',       dot: 'bg-emerald-500', text: 'text-emerald-600' },
  cancelled:  { label: 'Cancelled',  dot: 'bg-gray-300',    text: 'text-gray-400'    },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function startOfDay(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setHours(0, 0, 0, 0);
  return d;
}

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const h    = d.getHours() % 12 || 12;
  const m    = d.getMinutes();
  const ampm = d.getHours() < 12 ? 'AM' : 'PM';
  return m === 0 ? `${h} ${ampm}` : `${h}:${String(m).padStart(2, '0')} ${ampm}`;
}

function dayKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatDayHeader(date) {
  const today = startOfDay(new Date());
  const d     = startOfDay(date);
  const diff  = Math.round((d - today) / 86400000);

  const label = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month:   'short',
    day:     'numeric',
  });

  if (diff === 0)  return `Today · ${label}`;
  if (diff === 1)  return `Tomorrow · ${label}`;
  if (diff === -1) return `Yesterday · ${label}`;
  return label;
}

function formatWindowLabel(start, end) {
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear  = start.getFullYear() === end.getFullYear();
  const startStr  = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr    = end.toLocaleDateString('en-US', {
    month: sameMonth ? undefined : 'short',
    day:   'numeric',
    year:  sameYear ? undefined : 'numeric',
  });
  return `${startStr} – ${endStr}`;
}

// ── Component ────────────────────────────────────────────────────────────────

const CalendarMobileList = () => {
  const { tasks }        = useTasks();
  const { leads }        = useLeads();
  const { isTechnician } = useRole();
  const { user }         = useAuth();

  // Anchor = start of the 7-day window we're currently showing.
  const [anchorDate, setAnchorDate] = useState(() => startOfDay(new Date()));
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [sheetOpen, setSheetOpen]           = useState(false);

  const leadsMap = useMemo(() => {
    const m = {};
    leads.forEach((l) => { m[l.id] = l; });
    return m;
  }, [leads]);

  const windowEnd = useMemo(
    () => new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate() + 7),
    [anchorDate]
  );

  // All scheduled jobs inside the 7-day window, role-scoped.
  const windowJobs = useMemo(() => {
    const start = anchorDate.getTime();
    const end   = windowEnd.getTime();
    return tasks
      .filter((t) => {
        if (t.type !== 'job' || !t.scheduledStart) return false;
        const ts = new Date(t.scheduledStart).getTime();
        if (isNaN(ts)) return false;
        if (ts < start || ts >= end) return false;
        if (isTechnician && user?.uid) {
          return t.assignedTo === user.uid || !t.assignedTo;
        }
        return true;
      })
      .sort((a, b) => (a.scheduledStart || '').localeCompare(b.scheduledStart || ''));
  }, [tasks, anchorDate, windowEnd, isTechnician, user?.uid]);

  // Group by day key, preserving chronological order.
  const grouped = useMemo(() => {
    const map = new Map();
    windowJobs.forEach((job) => {
      const d   = new Date(job.scheduledStart);
      const key = dayKey(d);
      if (!map.has(key)) {
        map.set(key, { date: startOfDay(d), jobs: [] });
      }
      map.get(key).jobs.push(job);
    });
    return [...map.values()].sort((a, b) => a.date - b.date);
  }, [windowJobs]);

  const shiftWindow = (days) => {
    setAnchorDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days));
  };

  const openJob = (task) => {
    setSelectedTaskId(task?.id || null);
    setSheetOpen(true);
  };

  const closeSheet = () => {
    setSheetOpen(false);
    setSelectedTaskId(null);
  };

  // Derive selectedTask live from tasks so in-place updates (e.g. reassigning a
  // technician from JobAssignModal) immediately reflect in the still-open sheet.
  const selectedTask = useMemo(
    () => (selectedTaskId ? tasks.find((t) => t.id === selectedTaskId) : null),
    [selectedTaskId, tasks]
  );

  const selectedLead = selectedTask ? leadsMap[selectedTask.leadId] : null;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50 pb-20" data-testid="calendar-mobile-list">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div
        className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10"
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-[var(--cb-orange)]" />
            <h1 className="text-lg font-bold text-gray-900">Schedule</h1>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setAnchorDate(startOfDay(new Date()))}
              className="px-3 h-9 text-xs font-medium border border-gray-200 rounded-lg text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors"
              data-testid="calendar-mobile-today"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => shiftWindow(-7)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              data-testid="calendar-mobile-prev"
              aria-label="Previous week"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => shiftWindow(7)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              data-testid="calendar-mobile-next"
              aria-label="Next week"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-0.5" data-testid="calendar-mobile-range">
          {formatWindowLabel(anchorDate, new Date(windowEnd.getTime() - 86400000))}
          {' · '}
          {windowJobs.length} job{windowJobs.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* ── Grouped day sections ───────────────────────────────────────────── */}
      <div className="px-4 py-4 space-y-5">
        {grouped.length === 0 ? (
          <div className="text-center py-20" data-testid="calendar-mobile-empty">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <CalendarDays className="h-8 w-8 text-gray-300" />
            </div>
            <p className="text-gray-700 font-semibold">Nothing scheduled</p>
            <p className="text-gray-400 text-sm mt-1">
              No jobs in this 7-day window. Use desktop to plan your week.
            </p>
          </div>
        ) : (
          grouped.map(({ date, jobs }) => (
            <section key={dayKey(date)} data-testid={`calendar-mobile-day-${dayKey(date)}`}>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 px-1">
                {formatDayHeader(date)}
                <span className="ml-2 text-gray-300 font-medium normal-case tracking-normal">
                  {jobs.length} job{jobs.length !== 1 ? 's' : ''}
                </span>
              </h2>

              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                {jobs.map((job) => {
                  const lead    = leadsMap[job.leadId];
                  const cfg     = STATUS_CFG[job.status] || STATUS_CFG.scheduled;
                  const done    = job.status === 'completed';
                  const service = job.serviceType || lead?.service || '';
                  const name    = job.clientName || lead?.name || lead?.displayName || 'Unknown';

                  return (
                    <button
                      key={job.id}
                      type="button"
                      onClick={() => openJob(job)}
                      className="w-full flex items-start gap-3 px-4 py-3.5 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left"
                      data-testid={`calendar-mobile-job-${job.id}`}
                    >
                      {/* Time column — fixed width for clean vertical alignment */}
                      <div className="w-16 shrink-0 pt-0.5">
                        <div className="text-sm font-semibold text-gray-900 tabular-nums">
                          {fmtTime(job.scheduledStart) || '—'}
                        </div>
                      </div>

                      {/* Main column — name + service */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${done ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                          {name}
                        </p>
                        {service && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">{service}</p>
                        )}
                      </div>

                      {/* Status pill */}
                      <div className="flex items-center gap-1.5 shrink-0 pt-1">
                        <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                        <span className={`text-[11px] font-semibold ${cfg.text}`}>{cfg.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>

      {/* ── WorkOrderSheet ─────────────────────────────────────────────────── */}
      <WorkOrderSheet
        task={selectedTask}
        client={selectedLead}
        open={sheetOpen}
        onClose={closeSheet}
        onCompleted={closeSheet}
      />
    </div>
  );
};

export default CalendarMobileList;
